import { useState, useEffect, useMemo, useRef } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, Save, Download, ChevronLeft, ChevronRight, FileSpreadsheet, FileText, Check, Trash2, PiggyBank, Clock, Plus, CreditCard, Banknote, X, Camera, ShoppingBag, Building2, Mail, Loader2, CalendarDays, Gift, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const JOURS = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
const SECRET_CODE = '112118';

const SaisieCA = () => {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [salesData, setSalesData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [traiteurMensuel, setTraiteurMensuel] = useState(null);
  const [showTraiteurEdit, setShowTraiteurEdit] = useState(false);
  const [traiteurInput, setTraiteurInput] = useState('');
  const [savingTraiteur, setSavingTraiteur] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingDay, setEditingDay] = useState(null);
  const [form, setForm] = useState({
    ca_ht_20: '', ca_ht_10: '', ca_traiteur_ht: '', couverts: '',
    cb: '', especes: '', cheques: '', ancv: '', crt: '', attente: '', virement: '', bons_cadeaux: '',
    poche: ''
  });
  const [saving, setSaving] = useState(false);
  const [showPoche, setShowPoche] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState('');

  // Caisse state
  const [caisseSummary, setCaisseSummary] = useState(null);
  const [showRetraitForm, setShowRetraitForm] = useState(false);
  const [showDepotForm, setShowDepotForm] = useState(false);
  const [retraitForm, setRetraitForm] = useState({ montant: '', motif: '', photo: null });
  const [depotForm, setDepotForm] = useState({ montant: '', motif: '' });
  const [retraitLoading, setRetraitLoading] = useState(false);
  const [depotLoading, setDepotLoading] = useState(false);
  
  // Pending tables state
  const [pendingTables, setPendingTables] = useState([]);
  const [showAddTable, setShowAddTable] = useState(false);
  const [tableForm, setTableForm] = useState({ table_num: '', client_name: '', montant: '', note: '', date: new Date() });
  const [encaisseMode, setEncaisseMode] = useState(null); // table id being encaissé

  // TVA state
  const [tvaSummary, setTvaSummary] = useState(null);

  const handleCodeChange = (e) => {
    const value = e.target.value;
    setCode(value);
    if (value === SECRET_CODE) {
      setShowPoche(true);
      setShowCodeInput(false);
      setCode('');
      toast.success('Accès poche activé', { duration: 1500 });
    }
  };

  const handleLockClick = () => {
    if (showPoche) { setShowPoche(false); }
    else { setShowCodeInput(!showCodeInput); }
  };

  // Pending tables functions
  const fetchPendingTables = async () => {
    try {
      const res = await api.get('/pending-tables?status=attente');
      setPendingTables(res.data);
    } catch {}
  };

  useEffect(() => { fetchPendingTables(); }, []);

  const addPendingTable = async () => {
    if (!tableForm.table_num || !tableForm.client_name || !tableForm.montant) {
      toast.error('Remplir table, client et montant');
      return;
    }
    const d = tableForm.date || new Date();
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    try {
      await api.post('/pending-tables', {
        table_num: tableForm.table_num,
        client_name: tableForm.client_name,
        montant: parseFloat(tableForm.montant) || 0,
        note: tableForm.note,
        date_created: dateStr
      });
      toast.success(`Table ${tableForm.table_num} (${tableForm.client_name}) en attente`);
      setTableForm({ table_num: '', client_name: '', montant: '', note: '', date: new Date() });
      setShowAddTable(false);
      fetchPendingTables();
    } catch {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const encaisserTable = async (id, mode) => {
    try {
      const res = await api.put(`/pending-tables/${id}/encaisser`, { mode_paiement: mode });
      const { montant, mode: pMode } = res.data;
      toast.success(`Table encaissée : ${formatCurrency(montant)} en ${pMode === 'cb' ? 'CB' : pMode === 'virement' ? 'Virement' : 'Espèces'}`);
      setEncaisseMode(null);
      fetchPendingTables();
      fetchSales(); // Refresh daily sales to reflect new CA
    } catch {
      toast.error("Erreur");
    }
  };

  const deletePendingTable = async (id) => {
    try {
      await api.delete(`/pending-tables/${id}`);
      toast.success('Table supprimée');
      fetchPendingTables();
    } catch {
      toast.error("Erreur");
    }
  };

  const pendingTotal = pendingTables.reduce((sum, t) => sum + t.montant, 0);

  const [year, monthNum] = currentDate.split('-').map(Number);
  const monthName = MONTHS_FR[monthNum - 1];
  const daysInMonth = new Date(year, monthNum, 0).getDate();

  const fetchSales = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/daily-sales/${currentDate}`);
      setSalesData(res.data.days);
      setTotals(res.data.totals);
      setTraiteurMensuel(res.data.traiteur_mensuel || null);
      setTraiteurInput(res.data.traiteur_mensuel?.montant_ht || '');
    } catch {
      setSalesData([]);
      setTotals(null);
      setTraiteurMensuel(null);
    } finally {
      setLoading(false);
    }
  };

  const saveTraiteurMensuel = async () => {
    setSavingTraiteur(true);
    try {
      const res = await api.post('/traiteur-mensuel', {
        mois: currentDate,
        montant_ht: parseFloat(traiteurInput) || 0,
      });
      setTraiteurMensuel(res.data);
      setShowTraiteurEdit(false);
      toast.success('CA Traiteur du mois enregistré');
      fetchSales();
    } catch {
      toast.error('Erreur sauvegarde');
    }
    setSavingTraiteur(false);
  };

  useEffect(() => { fetchSales(); fetchCaisse(); fetchTva(); }, [currentDate]);

  const fetchCaisse = async () => {
    try {
      const res = await api.get(`/caisse/summary/${currentDate}`);
      setCaisseSummary(res.data);
    } catch {
      setCaisseSummary(null);
    }
  };

  const fetchTva = async () => {
    try {
      const res = await api.get(`/tva/summary/${currentDate}`);
      setTvaSummary(res.data);
    } catch {
      setTvaSummary(null);
    }
  };


  const addRetrait = async () => {
    if (!retraitForm.montant) { toast.error('Montant requis'); return; }
    setRetraitLoading(true);
    try {
      const formData = new FormData();
      formData.append('montant', parseFloat(retraitForm.montant));
      formData.append('motif', retraitForm.motif || '');
      formData.append('date', new Date().toISOString().split('T')[0]);
      formData.append('mois', currentDate);
      if (retraitForm.photo) {
        formData.append('photo', retraitForm.photo);
      }
      await api.post('/caisse/retrait', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Retrait ${retraitForm.montant}€ enregistre`);
      setRetraitForm({ montant: '', motif: '', photo: null });
      setShowRetraitForm(false);
      fetchCaisse();
    } catch {
      toast.error('Erreur retrait');
    } finally {
      setRetraitLoading(false);
    }
  };

  const deleteRetrait = async (id) => {
    try {
      await api.delete(`/caisse/retrait/${id}`);
      toast.success('Retrait supprime');
      fetchCaisse();
    } catch {
      toast.error('Erreur');
    }
  };

  const addDepot = async () => {
    if (!depotForm.montant) { toast.error('Montant requis'); return; }
    setDepotLoading(true);
    try {
      const formData = new FormData();
      formData.append('montant', parseFloat(depotForm.montant));
      formData.append('motif', depotForm.motif || 'Depot banque');
      formData.append('date', new Date().toISOString().split('T')[0]);
      formData.append('mois', currentDate);
      await api.post('/caisse/depot', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Depot ${depotForm.montant}€ enregistre`);
      setDepotForm({ montant: '', motif: '' });
      setShowDepotForm(false);
      fetchCaisse();
    } catch {
      toast.error('Erreur depot');
    } finally {
      setDepotLoading(false);
    }
  };

  const deleteDepot = async (id) => {
    try {
      await api.delete(`/caisse/depot/${id}`);
      toast.success('Depot supprime');
      fetchCaisse();
    } catch {
      toast.error('Erreur');
    }
  };

  const salesMap = useMemo(() => {
    const map = {};
    salesData.forEach(s => { map[s.date] = s; });
    return map;
  }, [salesData]);

  const navigateMonth = (dir) => {
    let m = monthNum + dir;
    let y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setCurrentDate(`${y}-${String(m).padStart(2, '0')}`);
    setEditingDay(null);
  };

  const openDay = async (day) => {
    const dateStr = `${currentDate}-${String(day).padStart(2, '0')}`;
    const existing = salesMap[dateStr];

    // Fetch bons cadeaux total for this day
    let bonsCadeauxTotal = 0;
    try {
      const bcRes = await api.get(`/bons-cadeaux/total-jour/${dateStr}`);
      bonsCadeauxTotal = bcRes.data.total || 0;
    } catch {}

    if (existing) {
      setForm({
        ca_ht_20: existing.ca_ht_20 || '',
        ca_ht_10: existing.ca_ht_10 || '',
        ca_traiteur_ht: existing.ca_traiteur_ht || '',
        couverts: existing.couverts || '',
        cb: existing.cb || '',
        especes: existing.especes || '',
        cheques: existing.cheques || '',
        ancv: existing.ancv || '',
        crt: existing.crt || '',
        attente: existing.attente || '',
        virement: existing.virement || '',
        bons_cadeaux: bonsCadeauxTotal || existing.bons_cadeaux || '',
        poche: existing.poche || '',
        fonds_de_caisse: existing.fonds_de_caisse || '',
        depot_banque: existing.depot_banque || '',
        depot_banque_date: '',
        poche_date: '',
      });
    } else {
      setForm({ ca_ht_20: '', ca_ht_10: '', ca_traiteur_ht: '', couverts: '', cb: '', especes: '', cheques: '', ancv: '', crt: '', attente: '', virement: '', bons_cadeaux: bonsCadeauxTotal || '', poche: '', fonds_de_caisse: '', depot_banque: '', depot_banque_date: '', poche_date: '' });
    }
    setEditingDay(day);
  };

  const saveDay = async () => {
    if (!editingDay) return;
    setSaving(true);
    const dateStr = `${currentDate}-${String(editingDay).padStart(2, '0')}`;
    const depotBanque = parseFloat(form.depot_banque) || 0;
    const poche = parseFloat(form.poche) || 0;
    // If depot/poche have a different date than the current day, we attach them to that target date instead
    const depotDate = form.depot_banque_date && form.depot_banque_date !== dateStr ? form.depot_banque_date : null;
    const pocheDate = form.poche_date && form.poche_date !== dateStr ? form.poche_date : null;
    try {
      await api.post('/daily-sales', {
        date: dateStr,
        ca_ht_20: parseFloat(form.ca_ht_20) || 0,
        ca_ht_10: parseFloat(form.ca_ht_10) || 0,
        ca_traiteur_ht: parseFloat(form.ca_traiteur_ht) || 0,
        couverts: parseInt(form.couverts) || 0,
        cb: parseFloat(form.cb) || 0,
        especes: parseFloat(form.especes) || 0,
        cheques: parseFloat(form.cheques) || 0,
        ancv: parseFloat(form.ancv) || 0,
        crt: parseFloat(form.crt) || 0,
        attente: parseFloat(form.attente) || 0,
        virement: parseFloat(form.virement) || 0,
        bons_cadeaux: parseFloat(form.bons_cadeaux) || 0,
        poche: pocheDate ? 0 : poche,
        fonds_de_caisse: parseFloat(form.fonds_de_caisse) || 0,
        depot_banque: depotDate ? 0 : depotBanque,
      });
      // If depot/poche have a different date, post them to that target date
      if (depotDate && depotBanque > 0) {
        const existing = await api.get(`/daily-sales/${depotDate.slice(0, 7)}`).then(r => (r.data || []).find(d => d.date === depotDate)).catch(() => null);
        await api.post('/daily-sales', {
          date: depotDate,
          ca_ht_20: existing?.ca_ht_20 || 0,
          ca_ht_10: existing?.ca_ht_10 || 0,
          couverts: existing?.couverts || 0,
          cb: existing?.cb || 0,
          especes: existing?.especes || 0,
          cheques: existing?.cheques || 0,
          ancv: existing?.ancv || 0,
          crt: existing?.crt || 0,
          attente: existing?.attente || 0,
          virement: existing?.virement || 0,
          bons_cadeaux: existing?.bons_cadeaux || 0,
          poche: existing?.poche || 0,
          fonds_de_caisse: existing?.fonds_de_caisse || 0,
          depot_banque: (existing?.depot_banque || 0) + depotBanque,
        });
      }
      if (pocheDate && poche > 0) {
        const existing = await api.get(`/daily-sales/${pocheDate.slice(0, 7)}`).then(r => (r.data || []).find(d => d.date === pocheDate)).catch(() => null);
        await api.post('/daily-sales', {
          date: pocheDate,
          ca_ht_20: existing?.ca_ht_20 || 0,
          ca_ht_10: existing?.ca_ht_10 || 0,
          couverts: existing?.couverts || 0,
          cb: existing?.cb || 0,
          especes: existing?.especes || 0,
          cheques: existing?.cheques || 0,
          ancv: existing?.ancv || 0,
          crt: existing?.crt || 0,
          attente: existing?.attente || 0,
          virement: existing?.virement || 0,
          bons_cadeaux: existing?.bons_cadeaux || 0,
          poche: (existing?.poche || 0) + poche,
          fonds_de_caisse: existing?.fonds_de_caisse || 0,
          depot_banque: existing?.depot_banque || 0,
        });
      }
      toast.success(`${editingDay} ${monthName} sauvegardé`);
      setEditingDay(null);
      fetchSales();
      fetchCaisse();
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteDay = async () => {
    if (!editingDay) return;
    const dateStr = `${currentDate}-${String(editingDay).padStart(2, '0')}`;
    try {
      await api.delete(`/daily-sales/${dateStr}`);
      toast.success('Supprimé');
      setEditingDay(null);
      fetchSales();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Erreur lors du telechargement');
    }
  };

  const exportExcel = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/month/${currentDate}`;
    downloadFile(url, `CA_LeMas_${currentDate}.xlsx`);
  };

  const exportCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/month-csv/${currentDate}`;
    downloadFile(url, `CA_LeMas_${currentDate}.csv`);
  };

  const exportTrimestre = () => {
    const trim = Math.ceil(monthNum / 3);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/quarter/${year}/${trim}`;
    downloadFile(url, `TVA_LeMas_T${trim}_${year}.xlsx`);
  };

  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailMenu, setShowEmailMenu] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ nom: '', email: '' });
  const [addingContact, setAddingContact] = useState(false);
  const emailMenuRef = useRef(null);

  useEffect(() => {
    api.get('/contacts-email').then(r => setContacts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (emailMenuRef.current && !emailMenuRef.current.contains(e.target)) setShowEmailMenu(false);
    };
    if (showEmailMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showEmailMenu]);

  const sendToContact = async (email, nom) => {
    setSendingEmail(true);
    setShowEmailMenu(false);
    try {
      const res = await api.post(`/export/recettes-email/${currentDate}`, { email });
      toast.success(`Recettes envoyees a ${nom || email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi email');
    } finally {
      setSendingEmail(false);
    }
  };

  const addContact = async () => {
    if (!newContact.email || !newContact.email.includes('@')) { toast.error('Email invalide'); return; }
    try {
      const res = await api.post('/contacts-email', newContact);
      setContacts(prev => [...prev, res.data]);
      setNewContact({ nom: '', email: '' });
      setAddingContact(false);
      toast.success('Contact ajoute');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const removeContact = async (id) => {
    try {
      await api.delete(`/contacts-email/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch {}
  };

  // Computed values for form preview
  const formPreview = useMemo(() => {
    const ht20 = parseFloat(form.ca_ht_20) || 0;
    const ht10 = parseFloat(form.ca_ht_10) || 0;
    const htTraiteur = parseFloat(form.ca_traiteur_ht) || 0;
    const totalHT = ht20 + ht10;
    const ttc20 = ht20 * 1.2;
    const ttc10 = ht10 * 1.1;
    const totalTTC = ttc20 + ttc10;
    const tva = totalTTC - totalHT;
    const totalHTAvecTraiteur = totalHT + htTraiteur;
    const totalTTCAvecTraiteur = totalTTC + (htTraiteur * 1.1);
    const encaiss = (parseFloat(form.cb) || 0) + (parseFloat(form.especes) || 0) + 
      (parseFloat(form.cheques) || 0) + (parseFloat(form.ancv) || 0) + 
      (parseFloat(form.crt) || 0) + (parseFloat(form.attente) || 0) +
      (parseFloat(form.virement) || 0) +
      (parseFloat(form.bons_cadeaux) || 0);
    const ecart = totalTTC - encaiss;
    return { totalHT, totalTTC, tva, ht: totalHT, encaiss, ecart, htTraiteur, totalHTAvecTraiteur, totalTTCAvecTraiteur };
  }, [form]);

  if (loading && salesData.length === 0) {
    return (
      <div className="flex justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-lemas-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-slide-up" data-testid="saisie-ca">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigateMonth(-1)} className="text-[rgb(var(--lm-text-secondary))] hover:text-[rgb(var(--lm-text))] p-1" data-testid="prev-month">
            <ChevronLeft size={20} />
          </button>
          <h2 className="font-title text-2xl" data-testid="month-title">
            {monthName} <span className="text-lemas-gold cursor-default select-none" onClick={handleLockClick} data-testid="poche-lock">{year}</span>
          </h2>
          <button onClick={() => navigateMonth(1)} className="text-[rgb(var(--lm-text-secondary))] hover:text-[rgb(var(--lm-text))] p-1" data-testid="next-month">
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportExcel} size="sm" variant="outline" className="border-lemas-green/30 text-lemas-green text-xs" data-testid="export-excel">
            <FileSpreadsheet size={14} className="mr-1" /> Excel
          </Button>
          <Button onClick={exportCSV} size="sm" variant="outline" className="border-[#A0ABAA]/30 text-[rgb(var(--lm-text-secondary))] text-xs" data-testid="export-csv">
            <FileText size={14} className="mr-1" /> CSV
          </Button>
          <Button onClick={exportTrimestre} size="sm" variant="outline" className="border-lemas-gold/30 text-lemas-gold text-xs" data-testid="export-trim">
            <Download size={14} className="mr-1" /> T{Math.ceil(monthNum / 3)}
          </Button>
          <div className="relative" ref={emailMenuRef}>
            <Button onClick={() => setShowEmailMenu(!showEmailMenu)} disabled={sendingEmail} size="sm" className="bg-lemas-gold text-lemas-bg text-xs" data-testid="send-comptable-btn">
              {sendingEmail ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />}
              Envoyer
            </Button>
            {showEmailMenu && (
              <div className="absolute right-0 top-full mt-1 w-72 bg-[rgb(var(--lm-surface-elevated))] border border-[rgb(var(--lm-border))] rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="email-menu">
                <div className="p-3 border-b border-[rgb(var(--lm-border))]">
                  <p className="text-xs font-title text-[rgb(var(--lm-text-secondary))]">Envoyer les recettes a :</p>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {contacts.map(c => (
                    <div key={c.id} className="flex items-center justify-between px-3 py-2 hover:bg-lemas-gold/5 group">
                      <button onClick={() => sendToContact(c.email, c.nom)} className="flex-1 text-left" data-testid={`send-to-${c.id}`}>
                        <span className="text-sm text-[rgb(var(--lm-text))]">{c.nom}</span>
                        <span className="text-[10px] text-[rgb(var(--lm-text-muted))] block">{c.email}</span>
                      </button>
                      <button onClick={() => removeContact(c.id)} className="opacity-0 group-hover:opacity-100 text-[rgb(var(--lm-text-muted))] hover:text-lemas-danger p-1">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {contacts.length === 0 && !addingContact && (
                    <p className="text-xs text-[rgb(var(--lm-text-muted))] text-center py-3">Aucun contact</p>
                  )}
                </div>
                {addingContact ? (
                  <div className="p-3 border-t border-[rgb(var(--lm-border))] space-y-2">
                    <input type="text" value={newContact.nom} onChange={e => setNewContact(p => ({...p, nom: e.target.value}))} placeholder="Nom (ex: Comptable)" className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-2 py-1.5 text-xs" data-testid="new-contact-nom" />
                    <input type="email" value={newContact.email} onChange={e => setNewContact(p => ({...p, email: e.target.value}))} placeholder="email@exemple.com" className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-2 py-1.5 text-xs font-mono" data-testid="new-contact-email" />
                    <div className="flex gap-2">
                      <Button onClick={addContact} size="sm" className="flex-1 bg-lemas-gold text-lemas-bg text-xs" data-testid="save-contact-btn">
                        <Check size={12} className="mr-1" /> Ajouter
                      </Button>
                      <Button onClick={() => { setAddingContact(false); setNewContact({ nom: '', email: '' }); }} size="sm" variant="ghost" className="text-xs">
                        <X size={12} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAddingContact(true)} className="w-full px-3 py-2.5 text-xs text-lemas-gold hover:bg-lemas-gold/5 border-t border-[rgb(var(--lm-border))] flex items-center gap-1" data-testid="add-contact-btn">
                    <Plus size={12} /> Ajouter un contact
                  </button>
                )}
              </div>
            )}
          </div>
          {showCodeInput && (
            <input type="password" value={code} onChange={handleCodeChange} placeholder="------" className="w-16 bg-transparent border-b border-[#5C6B6A] text-center text-xs font-mono focus:outline-none focus:border-lemas-gold" autoFocus maxLength={6} data-testid="poche-code-input" />
          )}
        </div>
      </div>

      {/* Totaux du mois — Restaurant / Traiteur / TOTAL */}
      {totals && totals.ca_ht_total > 0 && (
        <>
          {/* Détail Restaurant + Traiteur + Total */}
          <div className="grid grid-cols-3 gap-3">
            <div className="kpi-card text-center border-lemas-gold/30 bg-lemas-gold/5">
              <p className="kpi-label">🍽️ Restaurant HT</p>
              <p className="font-mono text-lg text-lemas-gold font-bold">{formatCurrency(totals.ca_ht_total)}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{totals.couverts} couverts</p>
            </div>
            <div className="kpi-card text-center border-emerald-500/30 bg-emerald-500/5">
              <p className="kpi-label">🥂 Traiteur HT</p>
              <p className="font-mono text-lg text-emerald-400 font-bold">{formatCurrency((totals.ca_traiteur_ht || 0) + (totals.traiteur_mensuel_ht || 0))}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">livraisons & événements</p>
            </div>
            <div className="kpi-card text-center border-2 border-blue-400/40 bg-blue-500/10">
              <p className="kpi-label">💰 TOTAL HT</p>
              <p className="font-mono text-xl text-blue-400 font-black">{formatCurrency(totals.ca_ht_total_avec_traiteur)}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">resto + traiteur</p>
            </div>
          </div>

          {/* KPI complémentaires */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="kpi-card text-center border-white/20">
              <p className="kpi-label">CA TTC Total</p>
              <p className="font-mono text-lg">{formatCurrency(totals.ca_ttc_total_avec_traiteur)}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">resto+traiteur</p>
            </div>
            <div className="kpi-card text-center border-red-400/20">
              <p className="kpi-label">TVA collectee</p>
              <p className="font-mono text-lg text-red-400">{formatCurrency(totals.tva_20 + totals.tva_10)}</p>
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">20%: {formatCurrency(totals.tva_20)} | 10%: {formatCurrency(totals.tva_10)}</p>
            </div>
            <div className="kpi-card text-center border-blue-400/20">
              <p className="kpi-label">Couverts</p>
              <p className="font-mono text-lg text-blue-400">{totals.couverts}</p>
            </div>
            <div className="kpi-card text-center border-lemas-green/20">
              <p className="kpi-label">Ticket moyen</p>
              <p className="font-mono text-lg text-lemas-green">{formatCurrency(totals.ticket_moyen)}</p>
            </div>
          </div>
        </>
      )}

      {/* Poche total du mois - secret */}
      {showPoche && totals && totals.poche > 0 && (
        <div className="kpi-card border-lemas-gold/30 bg-lemas-gold/5 flex items-center justify-between" data-testid="poche-monthly-total">
          <div className="flex items-center gap-2">
            <PiggyBank size={18} className="text-lemas-gold" />
            <span className="text-sm text-lemas-gold">Poche du mois</span>
          </div>
          <span className="font-mono text-xl text-lemas-gold font-medium">{formatCurrency(totals.poche)}</span>
        </div>
      )}

      {/* TRAITEUR MENSUEL - Single line per month */}
      <div className="kpi-card border-emerald-500/20" data-testid="traiteur-mensuel-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🥂</span>
            <div>
              <h3 className="font-title text-lg">CA Traiteur — {monthName}</h3>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Une seule ligne pour le mois — s'ajoute au CA pour la marge</p>
            </div>
          </div>
          {!showTraiteurEdit ? (
            <div className="flex items-center gap-3">
              <span className="font-mono text-xl text-emerald-400 font-bold" data-testid="traiteur-mensuel-val">
                {formatCurrency(traiteurMensuel?.montant_ht || 0)} HT
              </span>
              <Button
                onClick={() => { setShowTraiteurEdit(true); setTraiteurInput(traiteurMensuel?.montant_ht || ''); }}
                size="sm"
                className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                data-testid="edit-traiteur-mensuel-btn">
                {traiteurMensuel?.montant_ht ? 'Modifier' : 'Saisir'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={traiteurInput}
                onChange={(e) => setTraiteurInput(e.target.value)}
                placeholder="0"
                className="bg-emerald-50/10 border-emerald-500/30 font-mono w-32 text-right"
                autoFocus
                data-testid="input-traiteur-mensuel"
              />
              <span className="text-xs text-[rgb(var(--lm-text-muted))]">€ HT</span>
              <Button onClick={saveTraiteurMensuel} disabled={savingTraiteur} size="sm" className="bg-emerald-500 text-white" data-testid="save-traiteur-mensuel">
                {savingTraiteur ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              </Button>
              <Button onClick={() => setShowTraiteurEdit(false)} size="sm" variant="ghost">
                <X size={14} />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* SUIVI DE CAISSE */}
      {caisseSummary && (
        <div className="kpi-card border-green-500/20" data-testid="caisse-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Banknote size={18} className="text-green-400" />
              <h3 className="font-title text-lg">Suivi de caisse</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { setShowDepotForm(!showDepotForm); setShowRetraitForm(false); }}
                size="sm"
                className={showDepotForm ? "bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]" : "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"}
                data-testid="add-depot-btn"
              >
                {showDepotForm ? <X size={14} /> : <><Building2 size={14} className="mr-1" /> Depot</>}
              </Button>
              <Button
                onClick={() => { setShowRetraitForm(!showRetraitForm); setShowDepotForm(false); }}
                size="sm"
                className={showRetraitForm ? "bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]" : "bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"}
                data-testid="add-retrait-btn"
              >
                {showRetraitForm ? <X size={14} /> : <><ShoppingBag size={14} className="mr-1" /> Retrait</>}
              </Button>
            </div>
          </div>

          {/* Résumé caisse - comme le tableau Excel */}
          <div className="space-y-2 mb-3">
            {/* Ligne 1: Fond de caisse (ouverture) */}
            <div className="p-3 rounded-lg bg-yellow-500/10 flex items-center justify-between">
              <div>
                <p className="text-xs text-[rgb(var(--lm-text-muted))]">Fond de caisse</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">report mois precedent</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-lg text-yellow-400" data-testid="fonds-de-caisse-val">{formatCurrency(caisseSummary.fonds_de_caisse || 0)}</p>
                <button onClick={() => {
                  const val = prompt('Modifier le fond de caisse :', caisseSummary.fonds_de_caisse || 0);
                  if (val !== null && !isNaN(parseFloat(val))) {
                    api.post(`/caisse/fonds/${currentDate}`, { montant: parseFloat(val) })
                      .then(() => { toast.success('Fond de caisse mis a jour'); fetchCaisse(); })
                      .catch(() => toast.error('Erreur'));
                  }
                }} className="text-[9px] text-yellow-400/60 hover:text-yellow-400 underline" data-testid="edit-fonds-btn">
                  Modifier
                </button>
              </div>
            </div>

            {/* Ligne 2: + Espèces */}
            <div className="p-3 rounded-lg bg-green-500/10 flex items-center justify-between">
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">especes +</p>
              <p className="font-mono text-lg text-green-400">+{formatCurrency(caisseSummary.total_especes)}</p>
            </div>

            {/* Ligne 3: Fond de caisse (courant) = ouverture + especes */}
            <div className="p-3 rounded-lg bg-yellow-500/15 flex items-center justify-between border border-yellow-500/20">
              <div>
                <p className="text-xs font-medium text-yellow-400">Fond de caisse</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">ouverture + especes</p>
              </div>
              <p className="font-mono text-xl font-bold text-yellow-400" data-testid="fond-caisse-courant">
                {formatCurrency((caisseSummary.fonds_de_caisse || 0) + (caisseSummary.total_especes || 0))}
              </p>
            </div>

            {/* Ligne 4: - Depots banque */}
            <div className="p-3 rounded-lg bg-blue-500/10 flex items-center justify-between">
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">- depots banque</p>
              <p className="font-mono text-lg text-blue-400">{formatCurrency(caisseSummary.total_depots)}</p>
            </div>

            {/* Ligne 5: - Sorties caisse */}
            <div className="p-3 rounded-lg bg-orange-500/10 flex items-center justify-between">
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">- sorties caisse</p>
              <p className="font-mono text-lg text-orange-400">{formatCurrency(caisseSummary.total_retraits)}</p>
            </div>

            {/* Ligne 6: Solde caisse fin de mois */}
            <div className={`p-4 rounded-lg flex items-center justify-between border ${caisseSummary.solde_caisse >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <div>
                <p className="text-xs font-medium text-[rgb(var(--lm-text))]">Solde caisse</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">fin de mois = report mois suivant</p>
              </div>
              <p className={`font-mono text-2xl font-bold ${caisseSummary.solde_caisse >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="solde-caisse-val">
                {formatCurrency(caisseSummary.solde_caisse)}
              </p>
            </div>
          </div>

          {/* Formulaire dépôt */}
          {showDepotForm && (
            <div className="bg-lemas-bg/60 rounded-lg p-4 mb-3 space-y-3 animate-slide-up" data-testid="depot-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Montant</label>
                  <Input
                    type="number"
                    value={depotForm.montant}
                    onChange={(e) => setDepotForm(f => ({...f, montant: e.target.value}))}
                    placeholder="0"
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono"
                    data-testid="input-depot-montant"
                  />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Motif</label>
                  <Input
                    value={depotForm.motif}
                    onChange={(e) => setDepotForm(f => ({...f, motif: e.target.value}))}
                    placeholder="ex: depot banque"
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                    data-testid="input-depot-motif"
                  />
                </div>
              </div>
              <Button
                onClick={addDepot}
                disabled={depotLoading}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="confirm-depot"
              >
                <Building2 size={14} className="mr-2" /> {depotLoading ? 'Enregistrement...' : 'Enregistrer le depot'}
              </Button>
            </div>
          )}

          {/* Formulaire retrait */}
          {showRetraitForm && (
            <div className="bg-lemas-bg/60 rounded-lg p-4 mb-3 space-y-3 animate-slide-up" data-testid="retrait-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Montant</label>
                  <Input
                    type="number"
                    value={retraitForm.montant}
                    onChange={(e) => setRetraitForm(f => ({...f, montant: e.target.value}))}
                    placeholder="0"
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono"
                    data-testid="input-retrait-montant"
                  />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Motif</label>
                  <Input
                    value={retraitForm.motif}
                    onChange={(e) => setRetraitForm(f => ({...f, motif: e.target.value}))}
                    placeholder="ex: achat marche, courses..."
                    className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                    data-testid="input-retrait-motif"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Photo ticket/facture (optionnel)</label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] cursor-pointer hover:bg-[rgb(var(--lm-surface-elevated))] transition-colors text-sm">
                    <Camera size={14} />
                    {retraitForm.photo ? retraitForm.photo.name : 'Choisir photo'}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => setRetraitForm(f => ({...f, photo: e.target.files[0]}))}
                      data-testid="input-retrait-photo"
                    />
                  </label>
                  {retraitForm.photo && (
                    <Button size="sm" variant="ghost" onClick={() => setRetraitForm(f => ({...f, photo: null}))} className="text-red-400">
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
              <Button
                onClick={addRetrait}
                disabled={retraitLoading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-[rgb(var(--lm-text))]"
                data-testid="confirm-retrait"
              >
                <ShoppingBag size={14} className="mr-2" /> {retraitLoading ? 'Enregistrement...' : 'Enregistrer le retrait'}
              </Button>
            </div>
          )}

          {/* Liste retraits */}
          {caisseSummary.retraits && caisseSummary.retraits.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-[rgb(var(--lm-text-secondary))] font-medium">Retraits du mois</p>
              {caisseSummary.retraits.map(r => (
                <div key={r.id} className="flex items-center justify-between p-2 rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border))]" data-testid={`retrait-${r.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-xs text-[rgb(var(--lm-text-muted))] font-mono">{r.date?.slice(5)?.replace('-','/')}</span>
                    <span className="font-mono text-orange-400 font-medium">-{formatCurrency(r.montant)}</span>
                    {r.motif && <span className="text-sm text-[rgb(var(--lm-text-secondary))] truncate">{r.motif}</span>}
                    {r.photo && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${r.photo}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300">
                        <Camera size={14} />
                      </a>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteRetrait(r.id)} className="text-red-400/60 hover:text-red-400" data-testid={`delete-retrait-${r.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Liste depots */}
          {caisseSummary.depots && caisseSummary.depots.length > 0 && (
            <div className="space-y-2 mt-3">
              <p className="text-xs text-[rgb(var(--lm-text-secondary))] font-medium">Depots banque du mois</p>
              {caisseSummary.depots.map(d => (
                <div key={d.id} className="flex items-center justify-between p-2 rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border))]" data-testid={`depot-${d.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-xs text-[rgb(var(--lm-text-muted))] font-mono">{d.date?.slice(5)?.replace('-','/')}</span>
                    <span className="font-mono text-blue-400 font-medium">-{formatCurrency(d.montant)}</span>
                    {d.motif && <span className="text-sm text-[rgb(var(--lm-text-secondary))] truncate">{d.motif}</span>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteDepot(d.id)} className="text-red-400/60 hover:text-red-400" data-testid={`delete-depot-${d.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Section TVA */}
      {tvaSummary && (
        <div className="kpi-card border-purple-400/20" data-testid="tva-section">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={18} className="text-purple-400" />
            <h3 className="font-title text-lg">TVA du mois</h3>
          </div>

          <div className="space-y-2">
            {/* TVA Collectée (ventes) */}
            <div className="rounded-lg bg-purple-500/5 p-3 space-y-1">
              <p className="text-xs font-medium text-purple-400">TVA Collectee (ventes)</p>
              <div className="flex justify-between text-xs">
                <span className="text-[rgb(var(--lm-text-muted))]">Ventes HT 20% : {formatCurrency(tvaSummary.ventes.ht_20)}</span>
                <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(tvaSummary.ventes.tva_20)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[rgb(var(--lm-text-muted))]">Ventes HT 10% : {formatCurrency(tvaSummary.ventes.ht_10)}</span>
                <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(tvaSummary.ventes.tva_10)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium pt-1 border-t border-purple-400/20">
                <span className="text-purple-400">Total TVA collectee</span>
                <span className="font-mono text-purple-400">{formatCurrency(tvaSummary.ventes.total_tva_collectee)}</span>
              </div>
            </div>

            {/* TVA Déductible (achats) */}
            <div className="rounded-lg bg-blue-500/5 p-3 space-y-1">
              <p className="text-xs font-medium text-blue-400">TVA Deductible (achats)</p>
              <div className="flex justify-between text-xs">
                <span className="text-[rgb(var(--lm-text-muted))]">Achats HT</span>
                <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(tvaSummary.achats.total_ht)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[rgb(var(--lm-text-muted))]">Achats TTC</span>
                <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(tvaSummary.achats.total_ttc)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium pt-1 border-t border-blue-400/20">
                <span className="text-blue-400">Total TVA deductible</span>
                <span className="font-mono text-blue-400">{formatCurrency(tvaSummary.achats.total_tva_deductible)}</span>
              </div>
            </div>

            {/* TVA à payer */}
            <div className={`rounded-lg p-4 border ${tvaSummary.tva_a_payer >= 0 ? 'bg-red-500/10 border-red-500/20' : 'bg-green-500/10 border-green-500/20'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium text-[rgb(var(--lm-text))]">{tvaSummary.tva_a_payer >= 0 ? 'TVA a payer' : 'Credit TVA'}</p>
                  <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">collectee - deductible</p>
                </div>
                <p className={`font-mono text-2xl font-bold ${tvaSummary.tva_a_payer >= 0 ? 'text-red-400' : 'text-green-400'}`} data-testid="tva-a-payer">
                  {formatCurrency(Math.abs(tvaSummary.tva_a_payer))}
                </p>
              </div>
            </div>

            {/* Détail fournisseurs */}
            {tvaSummary.achats.detail?.length > 0 && (
              <details className="text-xs">
                <summary className="text-[rgb(var(--lm-text-muted))] cursor-pointer hover:text-[rgb(var(--lm-text-secondary))]">
                  Detail {tvaSummary.achats.detail.length} factures
                </summary>
                <div className="mt-1 space-y-0.5 pl-2">
                  {tvaSummary.achats.detail.map((d, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-[rgb(var(--lm-text-secondary))] truncate">{d.fournisseur}</span>
                      <span className="font-mono text-[rgb(var(--lm-text-muted))] shrink-0 ml-2">HT {d.ht} / TVA {d.tva}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      )}


      {/* Tables en attente */}
      <div className="kpi-card border-orange-400/20" data-testid="pending-tables-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-orange-400" />
            <h3 className="font-title text-lg">Tables en attente</h3>
            {pendingTables.length > 0 && (
              <span className="bg-orange-400/20 text-orange-400 text-xs font-mono px-2 py-0.5 rounded-full">
                {pendingTables.length} · {formatCurrency(pendingTotal)}
              </span>
            )}
          </div>
          <Button
            onClick={() => setShowAddTable(!showAddTable)}
            size="sm"
            className={showAddTable ? "bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]" : "bg-orange-400/20 text-orange-400 hover:bg-orange-400/30"}
            data-testid="add-pending-table-btn"
          >
            {showAddTable ? <X size={14} /> : <><Plus size={14} className="mr-1" /> Ajouter</>}
          </Button>
        </div>

        {/* Add form */}
        {showAddTable && (
          <div className="bg-lemas-bg/60 rounded-lg p-4 mb-4 space-y-3 animate-slide-up" data-testid="add-table-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">N° Table</label>
                <Input
                  value={tableForm.table_num}
                  onChange={(e) => setTableForm(f => ({...f, table_num: e.target.value}))}
                  placeholder="ex: 5"
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono"
                  data-testid="input-table-num"
                />
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Montant €</label>
                <Input
                  type="number"
                  value={tableForm.montant}
                  onChange={(e) => setTableForm(f => ({...f, montant: e.target.value}))}
                  placeholder="0"
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] font-mono"
                  data-testid="input-table-montant"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Nom du client</label>
              <Input
                value={tableForm.client_name}
                onChange={(e) => setTableForm(f => ({...f, client_name: e.target.value}))}
                placeholder="ex: M. Dupont"
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                data-testid="input-client-name"
              />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Note (optionnel)</label>
              <Input
                value={tableForm.note}
                onChange={(e) => setTableForm(f => ({...f, note: e.target.value}))}
                placeholder="ex: revient demain midi"
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm"
                data-testid="input-table-note"
              />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-mono bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))]" data-testid="table-date-picker">
                    <CalendarDays size={14} className="mr-2 text-orange-400" />
                    {tableForm.date
                      ? `${String(tableForm.date.getDate()).padStart(2,'0')}/${String(tableForm.date.getMonth()+1).padStart(2,'0')}/${tableForm.date.getFullYear()}`
                      : 'Choisir une date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" align="start">
                  <Calendar
                    mode="single"
                    selected={tableForm.date}
                    onSelect={(d) => d && setTableForm(f => ({...f, date: d}))}
                    className="text-[rgb(var(--lm-text))]"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button
              onClick={addPendingTable}
              className="w-full bg-orange-500 hover:bg-orange-600 text-[rgb(var(--lm-text))]"
              data-testid="confirm-add-table"
            >
              <Clock size={14} className="mr-2" /> Mettre en attente
            </Button>
          </div>
        )}

        {/* Pending tables list */}
        {pendingTables.length > 0 ? (
          <div className="space-y-2">
            {pendingTables.map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border))]" data-testid={`pending-table-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="bg-orange-400/20 text-orange-400 text-xs font-mono px-1.5 py-0.5 rounded">T{t.table_num}</span>
                    <span className="font-medium text-sm truncate">{t.client_name}</span>
                    <span className="font-mono text-orange-400">{formatCurrency(t.montant)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-orange-400/70 font-mono flex items-center gap-1">
                      <CalendarDays size={9} />
                      {t.date_created?.split('-').reverse().join('/')}
                    </span>
                    {t.note && <span className="text-[10px] text-[rgb(var(--lm-text-secondary))] italic truncate">{t.note}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  {encaisseMode === t.id ? (
                    <div className="flex items-center gap-1 animate-slide-up">
                      <Button size="sm" variant="ghost" onClick={() => encaisserTable(t.id, 'cb')} className="text-blue-400 hover:bg-blue-400/10 text-xs px-2" data-testid={`encaisser-cb-${t.id}`}>
                        <CreditCard size={12} className="mr-1" /> CB
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => encaisserTable(t.id, 'especes')} className="text-lemas-green hover:bg-lemas-green/10 text-xs px-2" data-testid={`encaisser-esp-${t.id}`}>
                        <Banknote size={12} className="mr-1" /> Esp
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => encaisserTable(t.id, 'virement')} className="text-purple-400 hover:bg-purple-400/10 text-xs px-2" data-testid={`encaisser-vir-${t.id}`}>
                        <ArrowRightLeft size={12} className="mr-1" /> Vir
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEncaisseMode(null)} className="text-[rgb(var(--lm-text-muted))] text-xs px-1">
                        <X size={12} />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEncaisseMode(t.id)} className="text-lemas-green hover:bg-lemas-green/10 text-xs" data-testid={`encaisser-btn-${t.id}`}>
                        <Check size={14} className="mr-1" /> Encaisser
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePendingTable(t.id)} className="text-lemas-danger hover:text-red-400" data-testid={`delete-table-${t.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-2">Aucune table en attente</p>
        )}
      </div>

      {/* Formulaire d'édition */}
      {editingDay && (
        <div className="kpi-card border-lemas-gold/30 bg-lemas-gold/5" data-testid="edit-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-title text-lg">
              {editingDay} {monthName} {year}
              <span className="ml-2 text-sm text-[rgb(var(--lm-text-secondary))] font-body">
                {JOURS[new Date(year, monthNum - 1, editingDay).getDay()]}
              </span>
            </h3>
            <div className="flex gap-2">
              <Button onClick={deleteDay} size="sm" variant="ghost" className="text-lemas-danger" data-testid="delete-day">
                <Trash2 size={14} />
              </Button>
              <Button onClick={() => setEditingDay(null)} size="sm" variant="ghost" className="text-[rgb(var(--lm-text-secondary))]">Fermer</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">CA HT 20% (repas)</label>
              <Input type="number" value={form.ca_ht_20} onChange={(e) => setForm(f => ({...f, ca_ht_20: e.target.value}))} className="bg-lemas-bg border-[rgb(var(--lm-border))] font-mono" placeholder="0" data-testid="input-ht20" />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">CA HT 10% (vente a emporter)</label>
              <Input type="number" value={form.ca_ht_10} onChange={(e) => setForm(f => ({...f, ca_ht_10: e.target.value}))} className="bg-lemas-bg border-[rgb(var(--lm-border))] font-mono" placeholder="0" data-testid="input-ht10" />
            </div>
            <div>
              <label className="text-xs text-emerald-500 block mb-1 font-bold">🥂 CA HT Traiteur</label>
              <Input type="number" value={form.ca_traiteur_ht} onChange={(e) => setForm(f => ({...f, ca_traiteur_ht: e.target.value}))} className="bg-emerald-50/10 border-emerald-500/30 font-mono" placeholder="0" data-testid="input-traiteur" />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Couverts</label>
              <Input type="number" value={form.couverts} onChange={(e) => setForm(f => ({...f, couverts: e.target.value}))} className="bg-lemas-bg border-[rgb(var(--lm-border))] font-mono" placeholder="0" data-testid="input-couverts" />
            </div>
          </div>

          {/* Preview TVA */}
          <div className="flex flex-wrap items-center gap-4 mb-4 p-3 rounded bg-[rgb(var(--lm-surface-elevated))] text-sm">
            <span className="text-[rgb(var(--lm-text-secondary))]">Restaurant HT: <span className="text-lemas-gold font-mono">{formatCurrency(formPreview.totalHT)}</span></span>
            {formPreview.htTraiteur > 0 && (
              <span className="text-emerald-500">Traiteur HT: <span className="font-mono">{formatCurrency(formPreview.htTraiteur)}</span></span>
            )}
            <span className="text-[rgb(var(--lm-text-secondary))] border-l border-[rgb(var(--lm-border))] pl-4">
              <strong>Total HT:</strong> <span className="text-emerald-400 font-mono font-bold">{formatCurrency(formPreview.totalHTAvecTraiteur)}</span>
            </span>
            <span className="text-[rgb(var(--lm-text-secondary))]">TTC: <span className="font-mono">{formatCurrency(formPreview.totalTTCAvecTraiteur)}</span></span>
            <span className="text-[rgb(var(--lm-text-secondary))]">TVA: <span className="text-red-400 font-mono">{formatCurrency(formPreview.tva)}</span></span>
          </div>

          <p className="text-xs text-[rgb(var(--lm-text-secondary))] mb-2 font-medium">Encaissements</p>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {[
              { key: 'cb', label: 'CB' },
              { key: 'especes', label: 'Espèces' },
              { key: 'cheques', label: 'Chèques' },
              { key: 'ancv', label: 'ANCV' },
              { key: 'crt', label: 'CRT' },
              { key: 'virement', label: 'Virement' },
              { key: 'attente', label: 'Attente' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="text-xs text-[rgb(var(--lm-text-muted))] block mb-1">{label}</label>
                <Input type="number" value={form[key]} onChange={(e) => setForm(f => ({...f, [key]: e.target.value}))} className="bg-lemas-bg border-[rgb(var(--lm-border))] font-mono text-sm" placeholder="0" data-testid={`input-${key}`} />
              </div>
            ))}
          </div>

          {/* Bons Cadeaux - pré-rempli automatiquement */}
          {(parseFloat(form.bons_cadeaux) > 0 || true) && (
            <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-[rgb(var(--lm-gold)/0.08)] border border-[rgb(var(--lm-gold)/0.2)]" data-testid="bons-cadeaux-field">
              <Gift size={16} className="text-[rgb(var(--lm-gold))] shrink-0" />
              <label className="text-xs text-[rgb(var(--lm-gold))] font-medium whitespace-nowrap">Bons Cadeaux</label>
              <Input
                type="number"
                value={form.bons_cadeaux}
                onChange={(e) => setForm(f => ({...f, bons_cadeaux: e.target.value}))}
                className="bg-transparent border-b border-[rgb(var(--lm-gold)/0.3)] font-mono text-sm text-[rgb(var(--lm-gold))] w-28 p-0 h-auto text-right"
                placeholder="0"
                data-testid="input-bons-cadeaux"
              />
              <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">€ (auto)</span>
            </div>
          )}

          {/* Ecart encaissements */}
          {Math.abs(formPreview.ecart) > 0.01 && formPreview.totalTTC > 0 && (
            <div className="flex items-center justify-center p-2 rounded bg-[rgb(var(--lm-surface-elevated))] mb-4">
              <span className={`text-sm font-mono ${Math.abs(formPreview.ecart) < 1 ? 'text-lemas-warning' : 'text-lemas-danger'}`}>
                Ecart: {formatCurrency(formPreview.ecart)}
              </span>
            </div>
          )}

          {/* Caisse & Banque */}
          <p className="text-xs text-[rgb(var(--lm-text-secondary))] mb-2 font-medium">Depot banque</p>
          <div className="grid grid-cols-1 gap-2 mb-4">
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-muted))] block mb-1">Montant</label>
              <Input type="number" value={form.depot_banque} onChange={(e) => setForm(f => ({...f, depot_banque: e.target.value}))} className="bg-lemas-bg border-[rgb(var(--lm-border))] font-mono text-sm" placeholder="0" data-testid="input-depot-banque" />
            </div>
            <div>
              <label className="text-xs text-[rgb(var(--lm-text-muted))] block mb-1">Date du depot (optionnel)</label>
              <Input
                type="date"
                value={form.depot_banque_date || `${currentDate}-${String(editingDay || 1).padStart(2, '0')}`}
                onChange={(e) => setForm(f => ({...f, depot_banque_date: e.target.value}))}
                className="bg-lemas-bg border-[rgb(var(--lm-border))] font-mono text-sm"
                data-testid="input-depot-banque-date"
              />
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">Par defaut: jour en cours. Changez si le depot a ete fait un autre jour.</p>
            </div>
          </div>

          {/* Argent de poche secret */}
          {showPoche && (
            <div className="p-3 rounded border border-lemas-gold/30 bg-lemas-gold/5 mb-4 space-y-2" data-testid="poche-field">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PiggyBank size={16} className="text-lemas-gold" />
                  <label className="text-xs text-lemas-gold font-medium">Poche</label>
                </div>
                <Input
                  type="number"
                  value={form.poche}
                  onChange={(e) => setForm(f => ({...f, poche: e.target.value}))}
                  placeholder="0"
                  className="bg-transparent border-b border-lemas-gold/30 text-right font-mono text-lemas-gold w-28 p-0 h-auto"
                  data-testid="input-poche"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-lemas-gold/70">Date cache (optionnel)</label>
                <Input
                  type="date"
                  value={form.poche_date || `${currentDate}-${String(editingDay || 1).padStart(2, '0')}`}
                  onChange={(e) => setForm(f => ({...f, poche_date: e.target.value}))}
                  className="bg-transparent border-b border-lemas-gold/30 text-right font-mono text-lemas-gold text-[11px] w-36 p-0 h-auto"
                  data-testid="input-poche-date"
                />
              </div>
            </div>
          )}

          <Button onClick={saveDay} disabled={saving} className="w-full bg-lemas-gold text-lemas-bg hover:bg-lemas-gold/90" data-testid="save-day">
            <Save size={16} className="mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      )}

      {/* Calendrier / Grille des jours */}
      <div className="kpi-card" data-testid="days-grid">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {JOURS.map(j => (
            <div key={j} className="text-center text-xs text-[rgb(var(--lm-text-muted))] py-1">{j}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for offset */}
          {Array.from({ length: new Date(year, monthNum - 1, 1).getDay() }, (_, i) => (
            <div key={`empty-${i}`} />
          ))}
          {/* Day cells */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${currentDate}-${String(day).padStart(2, '0')}`;
            const sale = salesMap[dateStr];
            const hasSale = sale && sale.ca_ht_total > 0;
            const isEditing = editingDay === day;
            const isToday = new Date().getDate() === day && new Date().getMonth() + 1 === monthNum && new Date().getFullYear() === year;
            
            return (
              <button
                key={day}
                onClick={() => openDay(day)}
                className={`p-2 rounded text-left transition-all min-h-[60px] ${
                  isEditing ? 'ring-2 ring-lemas-gold bg-lemas-gold/10' :
                  hasSale ? 'bg-lemas-green/10 hover:bg-lemas-green/20 border border-lemas-green/20' :
                  isToday ? 'bg-lemas-gold/5 border border-lemas-gold/20 hover:bg-lemas-gold/10' :
                  'bg-[rgb(var(--lm-surface-elevated))] hover:bg-[rgb(var(--lm-surface-elevated))] border border-[rgb(var(--lm-border))]'
                }`}
                data-testid={`day-${day}`}
              >
                <div className={`text-xs font-medium mb-1 ${isToday ? 'text-lemas-gold' : hasSale ? 'text-lemas-green' : 'text-[rgb(var(--lm-text-secondary))]'}`}>
                  {day}
                </div>
                {hasSale && (
                  <div className="text-xs font-mono text-lemas-green leading-tight">
                    {sale.ca_ht_total >= 1000 ? `${(sale.ca_ht_total / 1000).toFixed(1)}k` : `${sale.ca_ht_total}`}
                  </div>
                )}
                {hasSale && sale.couverts > 0 && (
                  <div className="text-[10px] text-[rgb(var(--lm-text-muted))]">{sale.couverts} couv.</div>
                )}
                {hasSale && (sale.cb > 0 || sale.especes > 0) && (
                  <div className="text-[9px] text-[rgb(var(--lm-text-muted))] leading-tight mt-0.5">
                    {sale.cb > 0 && <span className="text-blue-400">CB {sale.cb >= 1000 ? `${(sale.cb/1000).toFixed(1)}k` : sale.cb}</span>}
                    {sale.cb > 0 && sale.especes > 0 && <span> </span>}
                    {sale.especes > 0 && <span className="text-green-400">E {sale.especes >= 1000 ? `${(sale.especes/1000).toFixed(1)}k` : sale.especes}</span>}
                    {sale.bons_cadeaux > 0 && <span className="text-lemas-gold"> BC {sale.bons_cadeaux}</span>}
                  </div>
                )}
                {showPoche && hasSale && sale.poche > 0 && (
                  <div className="text-[10px] text-lemas-gold font-mono">{sale.poche}€</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table détaillée */}
      {salesData.length > 0 && (
        <div className="kpi-card overflow-x-auto" data-testid="sales-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgb(var(--lm-border))]">
                <th className="text-left py-2 px-2 text-[rgb(var(--lm-text-secondary))]">Date</th>
                <th className="text-right py-2 px-2 text-[rgb(var(--lm-text-secondary))]">HT 20%</th>
                <th className="text-right py-2 px-2 text-[rgb(var(--lm-text-secondary))]">HT 10%</th>
                <th className="text-right py-2 px-2 text-lemas-gold">Total HT</th>
                <th className="text-right py-2 px-2 text-red-400">TVA</th>
                <th className="text-right py-2 px-2 text-blue-400">Couv.</th>
                <th className="text-right py-2 px-2 text-[rgb(var(--lm-text-secondary))]">CB</th>
                <th className="text-right py-2 px-2 text-[rgb(var(--lm-text-secondary))]">Esp.</th>
              </tr>
            </thead>
            <tbody>
              {salesData.map(s => {
                const d = new Date(s.date);
                const jour = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'][d.getDay()];
                return (
                <tr key={s.date} className="border-b border-[rgb(var(--lm-border))] hover:bg-[rgb(var(--lm-surface-elevated))] cursor-pointer" onClick={() => openDay(parseInt(s.date.split('-')[2]))}>
                  <td className="py-2 px-2 font-mono text-xs">{jour} {s.date.split('-')[2]}/{s.date.split('-')[1]}</td>
                  <td className="py-2 px-2 text-right font-mono">{formatCurrency(s.ca_ht_20)}</td>
                  <td className="py-2 px-2 text-right font-mono">{formatCurrency(s.ca_ht_10)}</td>
                  <td className="py-2 px-2 text-right font-mono text-lemas-gold">{formatCurrency(s.ca_ht_total)}</td>
                  <td className="py-2 px-2 text-right font-mono text-red-400">{formatCurrency(s.tva_total)}</td>
                  <td className="py-2 px-2 text-right font-mono text-blue-400">{s.couverts}</td>
                  <td className="py-2 px-2 text-right font-mono text-[rgb(var(--lm-text-secondary))]">{formatCurrency(s.cb)}</td>
                  <td className="py-2 px-2 text-right font-mono text-[rgb(var(--lm-text-secondary))]">{formatCurrency(s.especes)}</td>
                </tr>
                );
              })}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="border-t-2 border-lemas-gold/30 bg-[rgb(var(--lm-surface))]-elevated/50">
                  <td className="py-3 px-2 font-title">TOTAL</td>
                  <td className="py-3 px-2 text-right font-mono font-medium">{formatCurrency(totals.ca_ht_20)}</td>
                  <td className="py-3 px-2 text-right font-mono font-medium">{formatCurrency(totals.ca_ht_10)}</td>
                  <td className="py-3 px-2 text-right font-mono font-medium text-lemas-gold">{formatCurrency(totals.ca_ht_total)}</td>
                  <td className="py-3 px-2 text-right font-mono text-red-400">{formatCurrency(totals.tva_20 + totals.tva_10)}</td>
                  <td className="py-3 px-2 text-right font-mono text-blue-400">{totals.couverts}</td>
                  <td className="py-3 px-2 text-right font-mono">{formatCurrency(totals.cb)}</td>
                  <td className="py-3 px-2 text-right font-mono">{formatCurrency(totals.especes)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default SaisieCA;
